import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import { getAudioGraphInstance } from '../audioEngine';
import units from "../units.json"
import { addKeysAsField } from '../utils/misc';
import { PianoRoller } from './PianoRoller';
import { limitToNyquist } from '../utils/parameters';
import { staticSelect } from '../utils/react';

export class BaseSynth extends InstrumentBase {
    poly = false;
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        ...PianoRoller.parametersDescription,
        // Synth params
        env_attack: {type: 'float', unit: units.second, min: 0.0, max: 2.0, initial: 0.01, bind: "preset"},
        env_decay: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 0.01, bind: "preset"},
        env_sustain: {type: 'float', min: 0.0, max: 1.0, initial: 1.0, bind: "preset"},
        env_release: {type: 'float', unit: units.second, min: 0.0, max: 5.0, initial: 0.01, bind: "preset"},
        osc_waveform: {type: 'enum', options: ['sine', 'triangle', 'square', 'sawtooth'], initial: 'sawtooth', bind: "preset"},
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 100, max: 15000, initial: 15000, logarithmic: true, bind: "preset"},
        lpf_q: {type: 'float', min: 0.0, max: 100.0, initial: 0.0, bind: "preset"},
        hpf_cutoff: {type: 'float', unit: units.hertz, min: 10, max: 3000, initial: 10, logarithmic: true, bind: "preset"},
        osc_detune: {type: 'float', min: 0.95, max: 1.05, initial: 1.0, bind: "preset"},
        lfo_rate: {type: 'float', unit: units.hertz, min: 0.1, max: 50.0, initial: 0.5, logarithmic: true, bind: "preset"},
        lfo_depth: {type: 'float', min: 0.0, max: 1.0, initial: 0.0, bind: "preset"},
        osc_octave: {type: 'int', min: -2, max: 2, step: 1, initial: 0, bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
    }

    async buildInstrumentAudioGraph({poly}) {
        await super.buildInstrumentAudioGraph();
        this.poly = poly;

        // Creem els nodes del graph i els connectem entre ells
        const hpf = new Tone.Filter(limitToNyquist(6000), "highpass", -24);
        const lpf = new Tone.Filter(limitToNyquist(15000), "lowpass", -24);
        const synth = poly ? new Tone.PolySynth(Tone.MonoSynth) : new Tone.DuoSynth();
        const lfo = new Tone.LFO({amplitude: 1.0, max: limitToNyquist(15000), min: 0});
        lpf.connect(hpf);
        synth.connect(lpf);
        
        // Settejem alguns paràmetres inicials que no canviaran
        if (poly) {
            synth.maxPolyphony = this.constructor.maxPolyphony;
            synth.set({
                attackCurve: "exponential",
                decayCurve: "exponential",
                releaseCurve: "exponential",
                volume: -20 // Avoid clipping, specially when using sine
            });
        } else {
            synth.set({
                vibratoAmount: 0.0,
                voice0: {
                    attackCurve: "exponential",
                    decayCurve: "exponential",
                    releaseCurve: "exponential"
                },
                voice1: {
                    attackCurve: "exponential",
                    decayCurve: "exponential",
                    releaseCurve: "exponential"
                },
                volume: -20 
            });
        }
        // LFO can't be connected to an underlying voice of a poly synth, so we connect it to the main lpf filter
        lfo.connect(lpf.frequency);
        lfo.start();

        // Afegeix els nodes al diccionari de nodes de l'instrument
        this.audioNodes = {
            synth: synth,
            hpf: hpf,
            lpf: lpf,
            lfo: lfo
        };

        // Crea els nodes d'efectes (això també els afegirà al diccionari de nodes de l'instrument)
        this.addEffectChainNodes(hpf, this.bus);
    }

    setParameterInAudioGraph(name, value) {
        switch (name) {
            case "lpf_cutoff":
                value = limitToNyquist(value);
                if (this.poly) {
                    this.audioNodes.synth.set({
                        'filterEnvelope': {'baseFrequency': value},
                    })
                    this.audioNodes.lpf.set({
                        'frequency': value,
                    })
                } else {
                    this.audioNodes.synth.set({
                        voice0: {'filterEnvelope': {'baseFrequency': value}},
                        voice1: {'filterEnvelope': {'baseFrequency': value}},
                    })
                }
                break;
            case "hpf_cutoff":
                this.audioNodes.hpf.frequency.rampTo(limitToNyquist(value), 0.01);
                break;
            case "lpf_q":
                if (this.poly) {
                    this.audioNodes.synth.set({
                        'filter': {'Q': value},
                    })
                } else {
                    this.audioNodes.synth.set({
                        voice0: {'filter': {'Q': value}},
                        voice1: {'filter': {'Q': value}},
                    })
                }
                break;
            case "lfo_rate":
                this.audioNodes.lfo.set({
                    'frequency': limitToNyquist(value),
                })
                break;
            case "lfo_depth":
                this.audioNodes.lfo.set({
                    'amplitude': value,
                })
                break;
            case "osc_detune":
                if (!this.poly) {
                    // This is only available when using DuoSynth
                    this.audioNodes.synth.set({'harmonicity': value});
                }
                break;
            case "env_attack":
            case "env_decay":
            case "env_sustain":
            case "env_release":
                const internalName = name.replace("env_", "");
                if (this.poly) {
                    this.audioNodes.synth.set({
                        'envelope': {[internalName]: value}, 'filterEnvelope': {[internalName]: value},
                    })
                } else {
                    this.audioNodes.synth.set({
                        voice0: {'envelope': {[internalName]: value}, 'filterEnvelope': {[internalName]: value}},
                        voice1: {'envelope': {[internalName]: value}, 'filterEnvelope': {[internalName]: value}},
                    })
                }
                break;
            case "osc_waveform":
                if (this.poly) {
                    this.audioNodes.synth.set({
                        'oscillator': { type: value },
                    })
                } else {
                    this.audioNodes.synth.set({
                        voice0: {'oscillator': { type: value }},
                        voice1: {'oscillator': { type: value }},
                    })
                }
                break;
        }
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        for (let i = 0; i < sequence.length; i++) {
            const note = sequence[i];
            if (note.onset === currentStep) {
                const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
                const velocity = (note.velocity ?? 127) / 127;
                let pitch = note.pitch;
                if (this.isMono) pitch = this.adjustPitchForWaveform(pitch);
                pitch += this.getPlaybackParameterValue("osc_octave") * 12;
                this.audioNodes.synth.triggerAttackRelease(Tone.Frequency(pitch, "midi"), duration * getAudioGraphInstance().get16BeatTime(), time, velocity);
            }
        }
    }

    adjustPitchForWaveform(pitch) {
        const waveform = this.getPlaybackParameterValue('osc_waveform');
        if (waveform === 'sine' || waveform === 'triangle') {
            return pitch + 12;
        }
        return pitch;
    }

    adjustPitchToInstrumentRange(pitch) {
        const minPitch = this.pitchRange.usable.min;
        const maxPitch = this.pitchRange.usable.max;

        // If note is outside range, make it be inside the range by wraping octaves
        if (pitch < minPitch) {
            const semitonesBelow = (pitch - minPitch) % 12;
            pitch = minPitch + semitonesBelow + 12;
        }
        if (pitch > maxPitch) {
            const semitonesAbove = (pitch - maxPitch) % 12;
            pitch = maxPitch + semitonesAbove - 12;
        } 
        return pitch;
    }
}